import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactMethods = () => {
  const contactMethods = [
    {
      id: 0,
      title: "Personal Email",
      description: "Direct personal email for casual inquiries and quick questions",
      icon: "Mail",
      // personal email for direct contact
      value: "mustan@mustan.dev",
      action: "Send Email",
      color: "cyan",
      responseTime: "Within 12 hours",
      bestFor: ["Quick Questions", "Casual Inquiries", "Follow-ups"]
    },
    {
      id: 1,
      title: "Direct Email",
      description: "For formal inquiries, job opportunities, and detailed project discussions",
      icon: "Mail",
      // direct email for contact — user requested info@mustan.dev
      value: "info@mustan.dev",
      action: "Send Email",
      color: "blue",
      responseTime: "Within 24 hours",
      bestFor: ["Job Opportunities", "Project Proposals", "Formal Inquiries"]
    },
    {
      id: 2,
      title: "LinkedIn",
      description: "Professional networking, career discussions, and industry connections",
      icon: "Linkedin",
      value: "https://www.linkedin.com/in/mustan-aliev-774732382/",
      action: "Connect",
      color: "cyan",
      responseTime: "Within 12 hours",
      bestFor: ["Professional Networking", "Career Advice", "Industry Insights"]
    },
    {
      id: 3,
      title: "Phone/WhatsApp",
      description: "Urgent matters, quick consultations, and real-time discussions",
      icon: "Phone",
      value: "No Number Provided",
      action: "Call Now",
      color: "emerald",
      responseTime: "Immediate",
      bestFor: ["Urgent Matters", "Quick Consultations", "Voice Calls"]
    },
    {
      id: 4,
      title: "GitHub",
      description: "Open source collaborations, code reviews, and technical discussions",
      icon: "Github",
      value: "https://github.com/Mustan-dev",
      action: "Follow",
      color: "slate",
      responseTime: "Within 48 hours",
      bestFor: ["Open Source", "Code Reviews", "Technical Discussions"]
    },
    {
      id: 5,
      title: "Instagram",
      description: "Daily development insights, project updates, and tech content sharing",
      icon: "Instagram",
      value: "https://www.instagram.com/mustan.dev/",
      action: "Follow",
      color: "fuchsia",
      responseTime: "Within 24 hours",
      bestFor: ["Tech Content", "Daily Updates", "Project Showcases"]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: "from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700",
      cyan: "from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700",
      emerald: "from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700",
      slate: "from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800",
      pink: "from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700",
      rose: "from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700",
      fuchsia: "from-fuchsia-500 to-fuchsia-600 hover:from-fuchsia-600 hover:to-fuchsia-700",
      indigo: "from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700"
    };
    return colors?.[color] || colors?.blue;
  };

  const [copiedMap, setCopiedMap] = useState({});

  const copyToClipboard = async (id, text) => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopiedMap((s) => ({ ...s, [id]: true }));
      setTimeout(() => setCopiedMap((s) => ({ ...s, [id]: false })), 2000);
    } catch (e) {
      // fallback: select + alert
      alert('Copy failed - please copy manually');
    }
  };

  const digitsOnly = (raw) => String(raw || '').replace(/[^+0-9]/g, '');

  const getActionHref = (method) => {
    const title = method?.title?.toLowerCase() || '';
    if (title.includes('email') || method?.value?.includes('@')) return `mailto:${method.value}`;
    if (title.includes('linkedin')) return method.value;
    if (title.includes('phone') || title.includes('whatsapp')) {
      const digits = digitsOnly(method.value).replace(/^\+/, '');
      return `tel:+${digits}`;
    }
    if (title.includes('github')) return method.value;
    return method.value;
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Multiple Ways to Connect
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Choose the communication method that works best for your needs. 
            Each channel is optimized for different types of conversations and response times.
          </p>
          {/* Location */}
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c1.657 0 3-1.343 3-3S13.657 5 12 5 9 6.343 9 8s1.343 3 3 3z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z" />
              </svg>
              <span>London, United Kingdom (GMT/BST)</span>
            </span>
          </div>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {contactMethods?.map((method) => (
            <div
              key={method?.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
            >
              <div className="p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${getColorClasses(method?.color)} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={method?.icon} size={24} color="white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{method?.title}</h3>
                      <p className="text-sm text-slate-500">{method?.responseTime}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {method?.description}
                </p>

                {/* Contact Value */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-slate-700 text-sm">{method?.value}</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Copy"
                        iconPosition="left"
                        className="text-slate-500 hover:text-slate-700"
                        onClick={() => copyToClipboard(method.id, method.value)}
                      >
                        {copiedMap[method.id] ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Best For Tags */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-slate-700 mb-3">Best for:</p>
                  <div className="flex flex-wrap gap-2">
                    {method?.bestFor?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {/* Primary action as a real link */}
                  <Button
                    asChild
                    variant="default"
                    fullWidth
                    iconName="ExternalLink"
                    iconPosition="right"
                    className={`bg-gradient-to-r ${getColorClasses(method?.color)} text-white border-0 hover:shadow-lg`}
                  >
                    <a href={getActionHref(method)} target="_blank" rel="noopener noreferrer">{method?.action}</a>
                  </Button>

                  {/* Additional WhatsApp button for phone entries */}
                  {method?.title?.toLowerCase().includes('phone') && (
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      iconName="MessageCircle"
                      className="border-slate-200"
                    >
                      <a
                        href={`https://wa.me/${digitsOnly(method.value).replace(/^\+/, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        WhatsApp
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Icon name="Clock" size={24} className="text-blue-500 mr-3" />
              <h3 className="text-xl font-bold text-slate-900">Availability & Timezone</h3>
            </div>
            <p className="text-slate-600 mb-4">
              Based in <strong>London, United Kingdom (GMT/BST)</strong> • Available Monday-Friday, 9 AM - 6 PM
            </p>
            <p className="text-sm text-slate-500">
              For urgent matters outside business hours, please use WhatsApp or mark your email as "URGENT"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMethods;
